'use client';

import { createContext, useContext, ReactNode } from 'react';
import { getRuntimeConfig } from '@lib/util/runtime-config-util';

export interface AppConfig {
    // API Configuration
    API_BASE_URL: string;
    MEDUSA_BACKEND_URL: string;
    MEDUSA_BACKEND_URL_CLIENT: string; // Public API Gateway URL for browser requests
    GTM_KEY: string;
    CLIENT_API_KEY: string;
    STRIPE_KEY: string;

    // Search Service Configuration
    SEARCH_SERVICE_URL: string;
    SEARCH_SERVICE_TOKEN: string;

    // Google APIs
    GOOGLE_API_KEY: string;

    // Environment
    HOST_ENV: string;

    // Feature Flags
    ENABLE_SCHEDULE_DELIVERY: string;
    ENABLE_GOOGLE_CAPTCHA: string;
    APP_VERSION: string;
    DISABLE_LOGIN_SIGNUP: string;
    STRIPE_ENABLE_OTHER_PAY: string;

    // Payment Configuration
    FISERV_PAYMENTJS_URL: string;
    USIO_APP_URL: string;
    GOOGLE_CAPTCHA_KEY: string;
    PAYPAL_CLIENT_ID: string;

    // Product Configuration
    VARIANT_CARD_CONFIG: string;

    // Analytics
    GA_MEASUREMENT_ID: string;

    // Medusa Configuration
    MEDUSA_API_KEY: string;
    REVALIDATE_WINDOW: string;

    // Search Configuration (Meilisearch/Algolia)
    SEARCH_API_KEY: string;
    SEARCH_ENDPOINT: string;
    SEARCH_INDEX_NAME: string;

    // Contact Information
    CUSTOMER_SERVICE_PHONE_NUMBER: string;
    GIFT_CARD_MARKET_EMAIL: string;

    // Service URLs and Tokens
    REDEMPTION_SERVICE_TOKEN: string;
    REDEMPTION_SERVICE_URL: string;

    //Redemption UI URL
    REDEMPTION_UI_URL:string

    // Logging
    ENABLE_VERBOSE_LOGGING: boolean;
}

// Client-safe config type (excludes server-only secrets)
export type ClientSafeConfig = Omit<AppConfig,
  'MEDUSA_BACKEND_URL' | 'SEARCH_SERVICE_TOKEN' | 
  'REDEMPTION_SERVICE_TOKEN' | 'USIO_APP_URL'>;

const ConfigContext = createContext<ClientSafeConfig | null>(null);

export interface ConfigProviderProps {
  readonly config: ClientSafeConfig;
  readonly children: ReactNode;
}

/**
 * Provider component for runtime configuration
 * On client-side, prefers window.__RUNTIME_CONFIG__ to ensure consistency with script tag
 * Falls back to server-provided config prop
 */
export function ConfigProvider({ config: serverConfig, children }: Readonly<ConfigProviderProps>) {
  // On client-side, prefer window.__RUNTIME_CONFIG__ to avoid hydration issues
  const config = typeof window !== 'undefined' && (window as any).__RUNTIME_CONFIG__
    ? getRuntimeConfig()
    : serverConfig;

  // Only log errors, not normal operation
  if (typeof window !== 'undefined') {
    const url = config?.MEDUSA_BACKEND_URL_CLIENT;
    if (!url || !url.startsWith('http')) {
      console.error('[ConfigProvider] ❌ INVALID MEDUSA_BACKEND_URL_CLIENT:', url);
    }
  }

  return (
    <ConfigContext.Provider value={config}>
      {children}
    </ConfigContext.Provider>
  );
}

/**
 * Hook to access runtime configuration in client components
 * Throws an error if used outside of ConfigProvider
 */
export function useConfig(): ClientSafeConfig {
  const config = useContext(ConfigContext);

  if (!config) {
    console.error('[useConfig] ❌ No config found - not inside ConfigProvider!');
    throw new Error('useConfig must be used within ConfigProvider');
  }

  return config;
}

/**
 * Hook to safely access runtime configuration in client components
 * Returns null if used outside of ConfigProvider
 */
export function useConfigSafe(): ClientSafeConfig | null {
  return useContext(ConfigContext);
}
